import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Package, Download } from 'lucide-react';
import { toast } from 'sonner';

export const Orders = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    api.get('/orders').then(res => setOrders(res.data)).finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const downloadInvoice = async (id, num) => {
    try {
      const res = await api.get(`/orders/${id}/invoice`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${num}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch (e) {
      if (e.response?.status === 409) {
        toast.info('Tax invoice will be available once payment is verified.');
      } else {
        toast.error('Failed to download invoice');
      }
    }
  };

  const invoiceReady = (o) =>
    !!o.invoice_number || o.payment_status === 'paid' ||
    (o.payment_method === 'cod' && o.order_status === 'delivered');

  const statusColor = (s) => s === 'delivered' ? 'bg-green-100 text-green-700' : s === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700';

  if (authLoading || !user) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-orange-800 mb-8">My Orders</h1>
      {loading ? <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div></div> : orders.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl">
          <Package size={64} className="mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-bold mb-2">No orders yet</h2>
          <Link to="/products" className="text-orange-600">Browse Products →</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(o => (
            <div key={o.id} className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex justify-between mb-4">
                <div><p className="font-bold text-lg">{o.order_number}</p><p className="text-sm text-gray-500">{new Date(o.created_at).toLocaleDateString()}</p></div>
                <div className="flex gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm ${statusColor(o.order_status)}`}>{o.order_status}</span>
                  <span className={`px-3 py-1 rounded-full text-sm ${o.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{o.payment_status}</span>
                </div>
              </div>
              <div className="flex gap-4 mb-4">
                {o.items.slice(0, 3).map((i, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <img src={i.product_image || 'https://images.pexels.com/photos/5702290/pexels-photo-5702290.jpeg?w=50'} className="w-10 h-10 rounded" alt="" />
                    <span className="line-clamp-1">{i.product_name}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center border-t pt-4">
                <p className="text-xl font-bold text-orange-600">₹{o.total}</p>
                {invoiceReady(o) ? (
                  <Button
                    data-testid={`download-invoice-${o.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => downloadInvoice(o.id, o.order_number)}
                    className="border-orange-600 text-orange-700 hover:bg-orange-50"
                  >
                    <Download size={16} className="mr-1" />
                    Tax Invoice
                  </Button>
                ) : (
                  <span
                    data-testid={`invoice-pending-${o.id}`}
                    className="text-xs text-gray-500 italic"
                    title="Invoice will appear once payment is verified"
                  >
                    Invoice generates after payment confirmation
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
